import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, FilePdf, FileDoc } from "@phosphor-icons/react";

const FORMAT_META = {
  pdf:  { label: "PDF",  icon: FilePdf, mime: "application/pdf" },
  docx: { label: "DOCX", icon: FileDoc, mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" },
  pptx: { label: "PPTX", icon: FileDoc, mime: "application/vnd.openxmlformats-officedocument.presentationml.presentation" },
  xlsx: { label: "XLSX", icon: FileDoc, mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
};

export default function DocViewTab({ doc, kind, title, renderBody, exports = [{ fmt: "pdf" }] }) {
  const pack = doc.pack;
  if (!pack) return <EmptyPack />;

  const download = async (fmt) => {
    const t = toast.loading(`Preparing ${fmt.toUpperCase()}…`);
    try {
      const resp = await api.get(`/investment-docs/${doc.id}/export/${kind}.${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const slug = (doc.company_name || "orixa-invest").replace(/\s+/g, "-").toLowerCase();
      a.download = `${slug}-${kind}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`, { id: t });
    } catch (err) {
      toast.error(extractApiError(err, "Download failed"), { id: t });
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-testid={`docview-${kind}`}>
      <div className="lg:col-span-3 bg-card border border-border rounded-2xl p-6 sm:p-10 card-elev">
        <div className="text-overline text-primary mb-3">Institutional</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight mb-8">{title}</h1>
        {renderBody(pack)}
      </div>
      <aside className="lg:col-span-1 lg:sticky lg:top-6 self-start space-y-4">
        <div className="bg-card border border-border rounded-2xl p-5 card-elev">
          <div className="text-overline text-muted-foreground mb-3">Downloads</div>
          <div className="space-y-2">
            {exports.map((e) => {
              const m = FORMAT_META[e.fmt] || FORMAT_META.pdf;
              return (
                <button
                  key={e.fmt}
                  onClick={() => download(e.fmt)}
                  className="w-full inline-flex items-center justify-between gap-2 border border-border rounded-lg px-3 py-2.5 text-sm font-semibold hover:bg-secondary transition-colors"
                  data-testid={`docview-download-${kind}-${e.fmt}`}
                >
                  <span className="inline-flex items-center gap-2"><m.icon size={16} weight="duotone" className="text-primary" /> {m.label}</span>
                  <DownloadSimple size={14} weight="bold" className="text-muted-foreground" />
                </button>
              );
            })}
          </div>
        </div>
      </aside>
    </div>
  );
}

function EmptyPack() {
  return (
    <div className="bg-card border border-border rounded-2xl p-12 text-center card-elev">
      <div className="font-display text-xl tracking-tight mb-2">Not generated yet</div>
      <p className="text-sm text-muted-foreground">Run AI Analysis to populate this document.</p>
    </div>
  );
}
